// @ts-check
import {
  Box,
  Flex,
  Button,
  SuccessIcon,
  WarningIcon,
  InfoIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Link,
} from '~/design-system';
import { TextField, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Trans } from '@lingui/macro';
import RawTrans from '../Trans';
import { bottlebooksFieldsSource } from '~/pages/imports/productAllInOneImport/[importId]/_config';
const EMPTY = '(empty)';

/**
 * Renders the table where the users can map all the imported values to bottlebooks valid values
 * @param {Object} props
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFields - current state
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFieldsWithValues - the options to be rendered on the select boxes
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFieldsWithoutAnyValues - the options to be rendered on the select boxes
 * @param {function} props.onChange - callback when one field is mapped
 * @param {function} props.onToggleSkip - callback when user wants to toggle skip in one field
 */
export default function FieldsTable({
  // This somehow doesn't feel write that we have to pass in the state and merge it here
  // This should probably be done in the hook.
  sourceFields,
  sourceFieldsWithValues,
  sourceFieldsWithoutAnyValues,
  onChange,
  onToggleSkip,
  ...rest
}) {
  return (
    <Table
      sx={{
        border: '1px solid',
        borderColor: 'brand.lightGray',
        width: '100%',
      }}
      {...rest}
    >
      <TableHead>
        <TableRow>
          <TableHeader
            sx={{ backgroundColor: 'brand.lightGray', width: '20%' }}
          >
            <Text variant="smallest">
              <Trans>Bottlebooks field name</Trans>
            </Text>
          </TableHeader>
          <TableHeader
            sx={{ backgroundColor: 'brand.lightGray', width: '20%' }}
          >
            <Text variant="smallest">
              <Trans>Import Values</Trans>
            </Text>
          </TableHeader>
          <TableHeader
            sx={{ backgroundColor: 'brand.lightGray', width: '50%' }}
          >
            <Text variant="smallest">
              <Trans>Match</Trans>
            </Text>
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sourceFieldsWithValues?.map((sourceField) => (
          <MapValuesTableRow
            sourceFields={sourceFields}
            sourceField={sourceField}
            onChange={onChange}
            onToggleSkip={onToggleSkip}
          />
        ))}
        {sourceFieldsWithoutAnyValues?.map((sourceField) => (
          <MapValuesTableRow
            sourceFields={sourceFields}
            sourceField={sourceField}
            onChange={onChange}
            onToggleSkip={onToggleSkip}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function MapValuesTableRow({
  sourceFields,
  sourceField,
  onChange,
  onToggleSkip,
}) {
  return (
    <TableRow key={sourceField.bottlebooksFieldName}>
      <TableCell sx={{ textAlign: 'left' }}>
        <MappedFieldLabel sourceField={sourceField} />
      </TableCell>

      <TableCell sx={{ textAlign: 'left', verticalAlign: 'top' }}>
        <ImportValues sx={{ lineHeight: '80px' }} sourceField={sourceField} />
      </TableCell>

      <TableCell sx={{ textAlign: 'left', verticalAlign: 'top' }}>
        <Match
          sx={{ lineHeight: '80px' }}
          sourceField={sourceField}
          onChange={(data) => {
            onChange({ ...data, sourceFields });
          }}
          onToggleSkip={
            onToggleSkip
              ? (data) => {
                  onToggleSkip({ ...data, sourceFields });
                }
              : null
          }
        />
      </TableCell>
    </TableRow>
  );
}

function MappedFieldLabel({ sourceField }) {
  const label =
    bottlebooksFieldsSource?.find(
      ({ key }) => key === sourceField.bottlebooksFieldName
    )?.label || sourceField.name;

  return (
    <Box>
      <Text>{label}</Text>
    </Box>
  );
}

/**
 * Renders the distinct values imported for the current source field.
 * @param {object} props
 * @param {object} props.sourceField - the current source field.
 * @returns
 */
function ImportValues({ sourceField, ...rest }) {
  if (!sourceField?.sourceFieldUniqueValues)
    return (
      <Flex sx={{ alignItems: 'center', height: '90px' }}>
        <Text>
          <Trans>Import does not contain any values for this column.</Trans>
        </Text>
      </Flex>
    );
  return (
    <Box>
      {sourceField.sourceFieldUniqueValues.map(
        ({ rawText, value, suggestedValues, isValueMappedToNull }, index) => {
          if (!rawText) return null;
          return (
            <Flex key={rawText} sx={{ alignItems: 'center' }}>
              {!!value ||
                (!!isValueMappedToNull && (
                  <SuccessIcon
                    sx={{ color: 'brand.successGreen', marginRight: 2 }}
                  />
                ))}
              {!value &&
                !isValueMappedToNull &&
                suggestedValues?.length > 0 && (
                  <WarningIcon
                    sx={{
                      color: 'brand.warningYellow',
                      width: '20px',
                      height: '20px',
                      marginRight: 2,
                    }}
                  />
                )}
              <Text sx={{ maxWidth: '280px' }} nowrap={1} {...rest}>
                {rawText}
              </Text>
            </Flex>
          );
        }
      )}
    </Box>
  );
}

/**
 * Renders autocomplete where the values can be matched with valid values.
 * @param {object} props
 * @param {object} props.sourceField - the current source field.
 * @param {function} props.onChange - the callback when any value is changed.
 * @param {function} props.onToggleSkip
 * @returns
 */
function Match({ sourceField, onChange, onToggleSkip, ...rest }) {
  const { validValues } = sourceField;
  if (!validValues?.length) return null;
  if (!sourceField?.sourceFieldUniqueValues) return null;
  return (
    <Box>
      {sourceField.sourceFieldUniqueValues.map((sourceField) => {
        let { isSuggestion } = sourceField;
        let suggestedValue = sourceField?.suggestedValues?.[0]?.value;
        let isSuggested =
          !sourceField.value &&
          suggestedValue &&
          sourceField?.value !== suggestedValue;

        // Near-unique substring matches (raw "Elgin" -> key "Elgin Valley")
        // are committed into the value in afterQuery (MapValues.jsx), so the
        // dropdown only ever displays what validation actually sees.
        const initialValue = sourceField.value || suggestedValue;
        return (
          <Flex
            key={sourceField.bottlebooksFieldName}
            sx={{ height: '80px' }}
            align="center"
          >
            <Box sx={{ width: '60%' }}>
              <Options
                options={validValues}
                initialValue={initialValue}
                disabled={sourceField.isValueMappedToNull}
                onChange={(option) => {
                  onChange({
                    option,
                    sourceField: { ...sourceField, isSuggestion },
                  });
                }}
              />
            </Box>
            <Flex
              sx={{
                width: '40%',
                paddingLeft: 3,
                alignItems: 'center',
              }}
            >
              {!isSuggested && isSuggestion && (
                <Tooltip
                  title={
                    <Trans>
                      "{sourceField.rawText}" has been automatically matched
                    </Trans>
                  }
                  placement="right-end"
                >
                  <Button
                    sx={{
                      width: 'fit-content',
                      border: 'none',
                      paddingLeft: '0px',
                    }}
                  >
                    <InfoIcon
                      sx={{
                        color: 'brand.lightBlue',
                        height: '20px',
                        width: '20px',
                      }}
                    />
                  </Button>
                </Tooltip>
              )}
              {!initialValue && (
                <>
                  <Tooltip
                    title={
                      sourceField?.isValueMappedToNull ? (
                        <Trans>
                          "{sourceField.rawText}" was skipped and won't be
                          imported
                        </Trans>
                      ) : (
                        <Trans>"{sourceField.rawText}" requires matching</Trans>
                      )
                    }
                    placement="right-end"
                  >
                    <Button
                      sx={{
                        width: 'fit-content',
                        border: 'none',
                        paddingLeft: '0px',
                        marginRight: '10px',
                      }}
                    >
                      <InfoIcon
                        sx={{
                          color: 'brand.lightBlue',
                          height: '20px',
                          width: '20px',
                        }}
                      />
                    </Button>
                  </Tooltip>

                  {onToggleSkip && (
                    <Link
                      onClick={() => {
                        onToggleSkip({
                          // option: { key: EMPTY },
                          sourceField: {
                            ...sourceField,
                            isSuggestion,
                          },
                        });
                      }}
                    >
                      <Text variant="smallest">
                        {sourceField?.isValueMappedToNull ? (
                          <Trans>Enable mapping</Trans>
                        ) : (
                          <Trans>Don't import this value</Trans>
                        )}
                      </Text>
                    </Link>
                  )}
                </>
              )}
              {isSuggested && (
                <Button
                  onClick={() => {
                    onChange({
                      option: { key: suggestedValue },
                      sourceField: {
                        ...sourceField,
                        isSuggestion,
                      },
                    });
                  }}
                  variant="primary"
                >
                  Confirm
                </Button>
              )}
            </Flex>
          </Flex>
        );
      })}
    </Box>
  );
}

/**
 * Renders one autocomplete component.
 * @param {object} props
 * @param {object[]} props.options
 * @param {string} props.initialValue
 * @param {function} props.onChange
 * @returns
 */
function Options({ options, initialValue, onChange, ...rest }) {
  return (
    <Autocomplete
      sx={{ width: '100%', ...rest }}
      options={options.filter(Boolean)}
      disablePortal
      getOptionLabel={(field) => {
        const fieldSource = options.find(
          ({ key }) => key === field?.key || key === field
        );
        return (fieldSource?.label || '').replace(/<0>(.*)<\/0>/, '$1');
      }}
      renderOption={(option) => {
        return (
          <RawTrans id={option?.label || ''} components={{ 0: <strong /> }} />
        );
      }}
      getOptionSelected={(option, selected) =>
        option?.key === (selected?.key || selected)
      }
      value={initialValue}
      onChange={(e, value) => {
        onChange(value);
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            name="mapValidValue"
            label=""
            variant="outlined"
            fullWidth
          />
        );
      }}
      {...rest}
    />
  );
}
